import { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { errorMessage, normalizeError } from '@/lib/errors';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: unknown;
  componentStack?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // Normalize error to ensure we always have safe strings
    const normalized = normalizeError(error, 'Unknown error');
    
    // Always log to console for production debugging
    console.error('[ErrorBoundary] Caught error:', {
      message: normalized.message,
      code: normalized.code,
      componentStack: errorInfo.componentStack,
    });
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    console.error('[ErrorBoundary] Raw error type:', typeof normalized.raw);
    console.error('[ErrorBoundary] Raw error keys:', normalized.raw && typeof normalized.raw === 'object' ? Object.keys(normalized.raw).slice(0, 10) : 'N/A');
    
    // Detect React error #31 (Objects are not valid as a React child)
    const err = error as any;
    if (err?.message?.includes('#31') || err?.message?.includes('Objects are not valid')) {
      console.error('[ErrorBoundary] ⚠️ REACT #31 DETECTED - Object rendered as child');
      console.error('[ErrorBoundary] Component stack (shows where object was rendered):', errorInfo.componentStack);
      console.error('[ErrorBoundary] Look for JSX like {obj} where obj is an object. Use obj.property or String(obj) instead.');
      
      // Try to extract the problematic value from the error message
      const match = err?.message?.match(/value:\s*({.*?})/);
      if (match) {
        console.error('[ErrorBoundary] Problematic value preview:', match[1]);
      }
    }
    
    // Expose error to window for debugging (safe, no circular refs)
    if (typeof window !== 'undefined') {
      try {
        (window as any).__DSOS_LAST_ERROR__ = {
          message: normalized.message,
          code: normalized.code,
          stack: errorInfo.componentStack,
          rawType: typeof normalized.raw,
          rawKeys: normalized.raw && typeof normalized.raw === 'object' ? Object.keys(normalized.raw).slice(0, 20) : null,
          timestamp: new Date().toISOString(),
        };
      } catch (e) {
        // Ignore if setting window property fails
      }
    }
    
    // Store in state for UI display
    this.setState({ error, componentStack: errorInfo.componentStack });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                Something went wrong
              </h2>
              <p className="text-sm text-muted-foreground">
                An unexpected error occurred. Please try again or reload the page.
              </p>
            </div>

            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={this.handleRetry}
                className="gap-2"
              >
                Try Again
              </Button>
              <Button
                onClick={this.handleReload}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </Button>
            </div>

            {this.state.error && (() => {
              const err = normalizeError(this.state.error, 'Unknown error');
              const debugEnabled = typeof window !== 'undefined' && 
                new URLSearchParams(window.location.search).get('debug') === '1';
              
              return (
                <details className="mt-4 text-left bg-muted/50 rounded-lg p-4">
                  <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                    Error Details {debugEnabled ? '(Debug Mode)' : '(Add ?debug=1 to URL for details)'}
                  </summary>
                  <pre className="mt-2 text-xs overflow-auto p-2 bg-background rounded text-destructive max-h-64 whitespace-pre-wrap">
                    {err.message}
                    {debugEnabled && this.state.componentStack && (
                      <>
                        {'\n\n'}
                        <div className="font-semibold mb-2 mt-4">Component Stack:</div>
                        {String(this.state.componentStack)}
                      </>
                    )}
                    {debugEnabled && err.raw && typeof err.raw === 'object' && 'stack' in err.raw && (
                      <>
                        {'\n\n'}
                        <div className="font-semibold mb-2 mt-4">Stack Trace:</div>
                        {String((err.raw as any).stack || '')}
                      </>
                    )}
                  </pre>
                  <div className="mt-2 text-xs text-muted-foreground">
                    💡 Check browser console for full error details and component stack
                  </div>
                </details>
              );
            })()}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
